import FileUploadField, { BlobData } from '@/components/ui/Form/FileUploadField';
import {
  ACCEPTABLE_MIME_TYPES,
  LIMIT_TEXT,
  MAX_BYTE_FILE_SIZE,
  MAX_NUM_OF_FILES
} from '@/components/ui/CreditTracker/util/constants';
import { useCallback } from 'react';
import { Certificate, CertificateBlobData, ICertificateViewModel } from '@/@types/client-api';
import useCertificates from '@/components/ui/Certificates/hooks/useCertificatesHook';
import { AnalyticsContext } from '@/analytics/constants';
import { trackDownloadEvent } from '@/analytics/certificates';
import Text from '@/components/cms/Text';
import useToast from '@/hooks/useToast';

export type CreditFormFileUploadProps = {
  externalCertificate?: Certificate | null;
  onChange: (cert: ICertificateViewModel) => void;
  context: AnalyticsContext;
  setValidationMessages: React.Dispatch<React.SetStateAction<string[] | undefined>>;
  validationMessages: string[] | undefined;
};
const CreditFormFileUpload = ({
  externalCertificate,
  onChange,
  setValidationMessages,
  context,
  validationMessages
}: CreditFormFileUploadProps) => {
  const { fileManagement } = useCertificates();
  const { showFailMessage } = useToast();

  const fileUpload = useCallback(
    async (files: File[]) => {
      const res = await fileManagement?.onFileUpload(files);

      const resData =
        res?.map(a => {
          return { blobUri: a.blobUri, fileName: a.fileName } as CertificateBlobData;
        }) ?? [];

      if (resData?.length) {
        onChange({
          certificateBlobs: [
            ...new Set(
              [...(externalCertificate?.certificateBlobs ?? []), ...resData]?.sort((a, b) =>
                (a?.blobUri ?? '') > (b?.blobUri ?? '')
                  ? 1
                  : (b?.blobUri ?? '') > (a?.blobUri ?? '')
                    ? -1
                    : 0
              )
            )
          ]
        });
      }
    },
    [fileManagement, onChange, externalCertificate?.certificateBlobs]
  );

  const validateFiles = useCallback(
    (files: File[]): string[] => {
      const result: string[] = [];

      if (files.length !== MAX_NUM_OF_FILES) {
        result.push('Only one file allowed.');
      }

      if (
        files?.some(a => !ACCEPTABLE_MIME_TYPES.includes(a.type)) ||
        files.some(a => a.size > MAX_BYTE_FILE_SIZE)
      ) {
        result.push(LIMIT_TEXT);
      }

      // Validate filename doesn't contain restricted characters
      const fileNameRegex = new RegExp(/[@$%&\\/:*?#]/);
      if (files.some(a => fileNameRegex.test(a.name))) {
        result.push('Characters @ $ % & \\ / : * ? # not allowed in filename.');
      }

      if (result?.length) {
        showFailMessage('There was an error uploading your file.');
      }

      setValidationMessages(result);
      return result;
    },
    [setValidationMessages, showFailMessage]
  );

  const fileDelete = useCallback(
    async (uri: string) => {
      const res = await fileManagement?.deleteFile(uri);

      if (res) {
        onChange({
          certificateBlobs: [
            ...new Set([
              ...(externalCertificate?.certificateBlobs?.filter(a => a.blobUri != uri) ?? [])
            ])
          ]
        });
      }
    },
    [externalCertificate?.certificateBlobs, fileManagement, onChange]
  );

  return (
    <>
      <p className="text-2-medium mt-5">Attachment (optional)</p>
      <Text element="p" className="text-2 mb-3 mt-1 block">
        {validationMessages?.length ? (
          validationMessages?.map(a => {
            return <span className="block w-full text-red">{a}</span>;
          })
        ) : (
          <span className="text-2-medium mb-4 text-gray-dark">{LIMIT_TEXT}</span>
        )}
      </Text>
      <FileUploadField
        acceptedMimeTypes={ACCEPTABLE_MIME_TYPES}
        initialBlobFileData={externalCertificate?.certificateBlobs?.map(a => {
          return { name: a.fileName, blobUri: a.blobUri } as BlobData;
        })}
        validateFiles={validateFiles}
        name="certificateFileUpload"
        onChange={fileUpload}
        onDownload={(blobName: string, fileName?: string) => {
          trackDownloadEvent(context);
          fileManagement?.downloadCertificateFile(blobName, fileName);
        }}
        onDelete={fileDelete}
        didError={fileManagement?.isUploadError}
        isUploading={fileManagement?.isFileUploading}
        isDeleting={fileManagement?.isFileDeleting}
        useProgressBar={true}
      />
    </>
  );
};

export default CreditFormFileUpload;
